import React, { useEffect, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import ExportData from 'highcharts/modules/export-data';
import HighchartsReact from 'highcharts-react-official';
import _ from 'lodash';


Exporting(Highcharts);
ExportData(Highcharts);

const chartOptionsBase = {
  time: {
    timezoneOffset: new Date().getTimezoneOffset()
  },
  chart: {
    panning: true,
    panKey: 'alt',
    zoomType: 'x',
    resetZoomButton: {
      position: {
        x: -24,
        y: 0
      }
    }
  },
  title: {
    text: ''
  },
  xAxis: {
    type: 'datetime'
  },
  yAxis: {
    title: {
      enabled: false
    }
  },
  legend: {
    align: 'center',
    verticalAlign: 'bottom',
    squareSymbol: false
  },
  plotOptions: {
    series: {
      turboThreshold: 0,
      marker: {
        enabled: false
      }
    }
  },
  credits: {
    enabled: false
  },
  exporting: {
    buttons: {
      contextButton: {
        menuItems:
        [
          // 'printChart',
          // 'separator',
          'downloadPNG',
          // 'downloadJPEG',
          // 'downloadPDF',
          'downloadSVG',
          'separator',
          'downloadCSV',
          'downloadXLS',
          // "viewData",
          'openInCloud']
      }
    }
  }
};

const constructDataSeries = (data, signalSets, colors) => {

  const mergedSignals = _.flatMap(signalSets, signalSet => {
    return _.flatMap(signalSet.signals, signal => {
      return {
        ...signal,
        assetId: signalSet.assetId
      };
    });
  });

  const seriesData = _.map(mergedSignals, (signal, index) => {
    let signalData;

    signalData = data.find(datum => datum.name === signal.name
      && datum.assetId === signal.assetId
      && (!signal.aggregate || datum.aggregate === signal.aggregate));

    return {
      name: signal.fullName ?? signal.name,
      type: 'line',
      showInLegend: true,
      tooltip: {
        valueDecimals: 2
      },
      data: signalData?.data,
      color: colors[index]
    };

  });

  return seriesData;
};

const isValidGraphDataPackage = (graphDataPackage) => {
  return graphDataPackage?.signalSets?.length > 0 && graphDataPackage?.data?.length > 0;
};

const CONTAINER_PROPS = {
  style: {
    height: '100%',
    width: '100%'
  }
};

const transformPlotlines = (plotlines, timestamp) => {
  const plotlineValues = _.map(plotlines, plotline => {
    return [timestamp, parseFloat(plotline.value)];
  });
  const scatterGraph = {
    name: 'plotlines',
    type: 'scatter',
    showInLegend: false,
    marker: {
      enabled: false,
      symbol: 'circle',
      radius: 5
    },
    data: plotlineValues
  };
  const updatedPlotlines = _.map(plotlines, plotline => {
    return {
      value: plotline.value,
      color: plotline.color,
      dashStyle: plotline.lineStyle,
      width: plotline.width,
      label: {
        text: plotline.displayName
      },
      zIndex: 3
    };
  });
  return [{ yAxis: { plotLines: updatedPlotlines } }, scatterGraph];
};

const transformChartOptions = (chartOptions, graphDataPackage, timespan, plotlines) => {
  let _chartOptions = _.cloneDeep(chartOptions);

  if (!isValidGraphDataPackage(graphDataPackage)) {
    _chartOptions.series = {
      name: '',
      data: []
    };
  } else {
    _chartOptions.series = constructDataSeries(
      graphDataPackage.data,
      graphDataPackage.signalSets,
      graphDataPackage.colors);

    _chartOptions.colors = graphDataPackage.colors;
  }

  if (timespan) {
    _.merge(_chartOptions, {
      xAxis: {
        min: timespan.from.toMillis(),
        max: timespan.to.toMillis()
      }
    });
  }
  if (plotlines && timespan) {
    const [pl, sg] = transformPlotlines(plotlines, timespan.to.toMillis());
    _.merge(_chartOptions, pl);
    if (_chartOptions.series.length > 0) {
      _chartOptions.series.push(sg);
    }
  }
  return _chartOptions;
};

const LineView = ({ graphDataPackage, timespan, resetZoom, setResetZoom, reflowTrigger, plotlines = [] }) => {
  const chartComponent = useRef(null);

  const chartOptions = useMemo(() => {
    return transformChartOptions(chartOptionsBase, graphDataPackage, timespan, plotlines);
  }, [graphDataPackage, plotlines, timespan]);

  useEffect(() => {
    if (chartComponent.current && resetZoom === true) {
      chartComponent.current.chart.xAxis[0].setExtremes();
      chartComponent.current.chart.yAxis[0].setExtremes();
      setResetZoom(false);
    }
  }, [resetZoom, setResetZoom]);

  useEffect(() => {
    if (chartComponent.current) {
      const chart = chartComponent.current.chart;
      chart.reflow();
    }
  }, [reflowTrigger]);

  return (
    <HighchartsReact
      ref={chartComponent}
      highcharts={Highcharts}
      options={chartOptions}
      containerProps={CONTAINER_PROPS}
    />
  );
};

export default LineView;
